import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Profile.css';

interface UserProfile {
  id: number;
  username: string;
  email: string;
  avatar?: string;
  role: string;
  coins: number;
  created_at?: string;
  phone?: string;
  recovery_email?: string;
  two_factor_enabled?: boolean;
}

interface UserStats {
  watched: number;
  parties: number;
  referrals: number;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState<UserStats>({ watched: 0, parties: 0, referrals: 0 });
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Form states
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Notification
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/profile');
    }
  }, [isAuthenticated, navigate]);

  // Load profile
  const loadProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data) {
          setProfile(data.data);
        }
      }

      // Load stats
      const statsResponse = await fetch('/api/users/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.status === 'success' && statsData.data) {
          setStats(statsData.data);
        }
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Show notification
  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle avatar upload
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/users/avatar', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.status === 'success') {
        showNotification('success', 'Avatar updated successfully!');
        loadProfile();
      } else {
        showNotification('error', data.message || 'Failed to update avatar');
      }
    } catch (error) {
      showNotification('error', 'Failed to upload avatar');
    }
  };

  // Handle email change
  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/users/email', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newEmail })
      });

      const data = await response.json();
      if (data.status === 'success') {
        showNotification('success', 'Email updated successfully!');
        setShowEmailModal(false);
        setNewEmail('');
        loadProfile();
      } else {
        showNotification('error', data.message || 'Failed to update email');
      }
    } catch (error) {
      showNotification('error', 'Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showNotification('error', 'Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showNotification('error', 'Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/users/password', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword 
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        showNotification('success', 'Password updated successfully!');
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showNotification('error', data.message || 'Failed to update password');
      }
    } catch (error) {
      showNotification('error', 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Get avatar URL
  const getAvatarUrl = (avatar?: string): string => {
    if (!avatar) return '/public/assets/images/default-avatar.png';
    if (avatar.startsWith('http')) return avatar;
    return `/public/uploads/avatars/${avatar}`;
  };

  if (!isAuthenticated) return null;

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              {profile?.avatar ? (
                <img src={getAvatarUrl(profile.avatar)} alt={profile.username} />
              ) : (
                <i className="fas fa-user"></i>
              )}
            </div>
            <button 
              className="avatar-upload-btn" 
              onClick={() => document.getElementById('avatarInput')?.click()}
            >
              <i className="fas fa-camera"></i>
            </button>
            <input 
              type="file" 
              id="avatarInput" 
              accept="image/*" 
              style={{ display: 'none' }}
              onChange={handleAvatarUpload}
            />
          </div>
          <h1 className="profile-name">{loading ? 'Loading...' : profile?.username || 'User'}</h1>
          <p className="profile-email">{profile?.email || ''}</p>
        </div>

        <div className="profile-grid">
          {/* Account Info Card */}
          <div className="profile-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fas fa-user-circle"></i>
              </div>
              <h3 className="card-title">Account Information</h3>
            </div>
            
            <div className="profile-info-item">
              <span className="info-label">
                <i className="fas fa-user"></i>
                Username
              </span>
              <span className="info-value">{profile?.username || '-'}</span>
            </div>
            
            <div className="profile-info-item">
              <span className="info-label">
                <i className="fas fa-envelope"></i>
                Email
              </span>
              <span className="info-value">{profile?.email || '-'}</span>
            </div>
            
            <div className="profile-info-item">
              <span className="info-label">
                <i className="fas fa-calendar"></i>
                Member Since
              </span>
              <span className="info-value">{formatDate(profile?.created_at)}</span>
            </div>
            
            <div className="profile-info-item">
              <span className="info-label">
                <i className="fas fa-shield-alt"></i>
                Role
              </span>
              <span className="info-value" style={{ textTransform: 'capitalize' }}>{profile?.role || '-'}</span>
            </div>
            
            <button 
              className="btn btn-secondary btn-full" 
              style={{ marginTop: '1rem' }}
              onClick={() => setShowEmailModal(true)}
            >
              <i className="fas fa-edit"></i>
              Change Email
            </button>
          </div>

          {/* Security Card */}
          <div className="profile-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="card-title">Security</h3>
            </div>
            
            <div className="profile-info-item">
              <span className="info-label">
                <i className="fas fa-key"></i>
                Password
              </span>
              <span className="info-value">••••••••</span>
            </div>
            
            <div className="profile-info-item">
              <span className="info-label">
                <i className="fas fa-mobile-alt"></i>
                Two-Factor Auth
              </span>
              <span className={`badge ${profile?.two_factor_enabled ? 'badge-success' : 'badge-warning'}`}>
                {profile?.two_factor_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            
            <button 
              className="btn btn-secondary btn-full" 
              style={{ marginTop: '1rem' }}
              onClick={() => setShowPasswordModal(true)}
            >
              <i className="fas fa-lock"></i>
              Change Password
            </button>
          </div>

          {/* Statistics Card */}
          <div className="profile-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="card-title">Statistics</h3>
            </div>
            
            <div className="stats-grid">
              <div className="stat-box">
                <div className="stat-value">{profile?.coins || 0}</div>
                <div className="stat-label">Coins</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{stats.watched}</div>
                <div className="stat-label">Watched</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{stats.parties}</div>
                <div className="stat-label">Parties</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{stats.referrals}</div>
                <div className="stat-label">Referrals</div>
              </div>
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="profile-card">
            <div className="card-header">
              <div className="card-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <h3 className="card-title">Quick Actions</h3>
            </div>
            
            <div className="action-buttons">
              <button className="btn btn-secondary btn-full" onClick={() => navigate('/settings')}>
                <i className="fas fa-cog"></i>
                Settings
              </button>
              <button className="btn btn-secondary btn-full" onClick={() => navigate('/subscribe')}>
                <i className="fas fa-crown"></i>
                Subscription
              </button>
              <button className="btn btn-secondary btn-full" onClick={() => navigate('/watchparty')}>
                <i className="fas fa-users"></i>
                Watch Parties
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Change Email</h2>
              <button className="modal-close" onClick={() => setShowEmailModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleEmailChange}>
              <div className="form-group">
                <label className="form-label">New Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : 'Update Email'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Change Password</h2>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`notification show ${notification.type}`}>
          <i className={`fas fa-${notification.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          <span>{notification.message}</span>
        </div>
      )}
    </div>
  );
};

export default Profile;
